import React, { useEffect, useCallback, useMemo, forwardRef, useState } from 'react'
import { usePatient, useScreen } from 'hooks/useURL'
import PropTypes from 'prop-types'
import Help from '../Help/Help.jsx'
import { useSelector, useDispatch } from 'react-redux'
import { openModal, closeModal } from 'actions/modalActions'
import Loading from '../Loading/Loading.jsx'
import { updateWindow } from 'actions/interactableActions'
import FavoriteButton from 'GeneralUI/FavoriteButton/FavoriteButton.jsx'
import EditWindowModal from '../../Designer/EditWindowModal.jsx'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { getCurrentScreenType } from 'Helper/screenTypeHelpers'
import ArrangeWindowTile from '../../Config/General/ArrangeWindowTile.jsx'

const Window = forwardRef((props, ref) => {
  const nullFunction = useCallback(() => {
    return null
  }, [])

  const container = useSelector(state => state.interactableState.container)
  const onScreenWindows = useSelector(state => state.interactableState.windows)
  const dispatch = useDispatch()
  const designMode = useSelector(state => state.interactableState.uiDesignMode)

  const [prevSize, setPrevSize] = useState(null)

  const {
    children,
    privilegeErrorMessage,
    hasPrivileges,
    className = null,
    title = null,
    loading,
    helpId = false,
    closeable = true,
    draggable = true,
    resizeable = true,
    isLocked = false,
    zIndexHandler = nullFunction,
    closeCallback,
    closeHandler,
    windowConfig,
    nextStep,
    nextAction,
    nextEnabled,
    nextText
  } = props
  const patientId = usePatient()
  const { asModal = false, color = {}, context: windowContext = {} } = windowConfig
  const { canFavorite = true } = windowContext
  const screenType = getCurrentScreenType() === 'ipad' ? 'IPad' : 'Standard'

  const nextVisible =
    nextAction != null &&
    windowConfig.nextButton === 'header' &&
    !asModal

  let useableNextAction =
    nextAction == null
      ? nextStep
      : nextAction

  useableNextAction = !(nextEnabled && !loading) ? nullFunction : useableNextAction

  let nextButtonClass = 'btn btn-default btn-workflow-next'
  nextButtonClass = (nextEnabled && !loading)
    ? nextButtonClass
    : nextButtonClass + ' disabled'

  const headerStyle = { backgroundColor: '#DEDCFF', borderColor: '#DEDCFF', color: '#FFF' }
  const footerStyle = { backgroundColor: '#DEDCFF', borderColor: '#DEDCFF', color: '#FFF' }

  if (isLocked) {
    headerStyle.backgroundColor = 'rgba(255,100,50,1)'
    headerStyle.borderColor = 'rgba(255,100,50,1)'
    footerStyle.backgroundColor = 'rgba(255,100,50,1)'
    footerStyle.borderColor = 'rgba(255,100,50,1)'
  } else if (color.header != null) {
    headerStyle.backgroundColor = color.header
    headerStyle.borderColor = color.header
    footerStyle.backgroundColor = color.footer
    footerStyle.borderColor = color.footer
  }

  if (headerStyle.backgroundColor === '#DEDCFF' || headerStyle.backgroundColor === '#D5B5FF') {
    headerStyle.color = '#3B1968'
  }

  if (footerStyle.backgroundColor === '#DEDCFF' || footerStyle.backgroundColor === '#D5B5FF') {
    footerStyle.color = '#3B1968'
  }

  const closeWrapper = useCallback(() => {
    // if closeCallback is defined it is a function that returns a promise
    // that promise resolves with true or false, determining if the window is to be closed
    if (closeCallback) {
      closeCallback().then(doClose => {
        if (doClose) {
          closeHandler()
        }
      })
    } else {
      closeHandler()
    }
  }, [closeCallback, closeHandler])

  useEffect(() => {
    // componentDidMount
    zIndexHandler()
  }, [])

  const windowContent = useMemo(() => {
    if (loading) {
      return <Loading />
    } else if (!hasPrivileges) {
      return (
        <div className="insufficient-privileges">
          <FontAwesomeIcon icon={['far', 'eye-slash']} size="3x" color="red" />
          <br />
          {privilegeErrorMessage}
        </div>
      )
    } else {
      return React.Children.toArray(children)
        .filter(child => child != null)
        .map(child => {
          if (typeof child.type === 'string') {
            // dom element so don't pass props
            return React.cloneElement(child)
          } else {
            return React.cloneElement(child, { closeSelf: closeWrapper })
          }
        })
    }
  }, [loading, patientId, hasPrivileges, privilegeErrorMessage, children, closeWrapper])

  const adjustWindowSize = useCallback(e => {
    // when maximizing prevSize stores the original size so we know what to restore to.
    // if its null that means we are at the "normal" size and are doing maximize
    const { x, y, width, height, ...rest } = windowConfig
    if (!prevSize) {
      const { width: containerWidth, height: containerHeight } = container
      dispatch(updateWindow({
        width: containerWidth,
        height: containerHeight,
        x: 0,
        y: 0,
        ...rest
      }))
      setPrevSize({ x, y, width, height })
    } else {
      dispatch(updateWindow({ ...prevSize, ...rest }))
      setPrevSize(null)
    }
  }, [
    container.width,
    container.height,
    windowConfig.x,
    windowConfig.y,
    windowConfig.width,
    windowConfig.height,
    prevSize
  ])
  const editWindowModal = useCallback(() => {
    const closeSelf = () => dispatch(closeModal('edit_window_modal'))
    dispatch(
      openModal('edit_window_modal', {
        title: 'Configure ' + screenType + ' Window',
        content: (
          <EditWindowModal
            windowConfig={windowConfig}
            closeSelf={closeSelf}
          />
        ),
        closeable: true
      })
    )
  }, [windowConfig, container])

  const tileWindow = () => {
    dispatch(openModal('arrange_window_tile_modal', {
      title: 'Arrange Window Tile',
      content: (
        <ArrangeWindowTile
          windowConfig={windowConfig}
          name={windowConfig.ref}
        />
      ),
      closeable: true
    }))
  }

  const adjustSizeIcon = prevSize == null ? 'window-maximize' : 'window-minimize'
  return (
    <div
      className={'draggable resizeable window ' + className}
      ref={ref}
      onMouseDown={zIndexHandler}
      data-windowname={windowConfig.ref}
    >
      <div className="list-component list-group window-window">
        <div
          style={headerStyle}
          className={`list-group-item active header ${isLocked ? 'locked' : ''
            }`}
          aria-label={`${title} window header`}
        >
          <div
            className={'header' + (draggable || asModal ? ' draggable-handle' : '')}
            aria-label={`${title} window header`}
          >
            <div className="corner-icons">
              {draggable || asModal
                ? <FontAwesomeIcon
                  icon={['fas', 'bars']}
                  className="draggable-handle"
                  aria-label={`${title} window drag icon`}
                />
                : <div />
              }
              {designMode
                ? <div
                  onClick={editWindowModal}
                >
                  <FontAwesomeIcon
                    icon={['fas', 'compass-drafting']}
                    className="designer-icon"
                    aria-label={'window designer icon'}
                  />
                </div>
                : <div />
              }
              <div className='empty'> </div>
            </div>
            <div
              className="list-group-title"
              style={headerStyle}
            >
              <span className='title'>
                {title}
              </span>
              {helpId
                ? <Help id={helpId} aria-label={`${title} window help icon`} />
                : null
              }
            </div>
            <div className="corner-icons">
              {nextVisible
                ? <button
                  onClick={useableNextAction}
                  className={`next-button ${nextButtonClass}`}
                >
                  {nextText || 'Next'}
                </button>
                : <div />
              }
              <FavoriteButton name={windowConfig.ref} type='window' canFavorite={canFavorite} />
              {designMode
                ? <div
                  onClick={tileWindow}
                >
                  <FontAwesomeIcon
                    icon={['fa', 'table-cells-large']}
                    className="designer-icon"
                    aria-label={'window designer icon'}
                  />
                </div>
                : <div />
              }
              {
                resizeable
                  ? <div className="adjust-window-size" onClick={adjustWindowSize}>
                    <FontAwesomeIcon
                      icon={['far', adjustSizeIcon]}
                      aria-label={`${adjustSizeIcon} window icon`}
                      className="size-button"
                    />
                  </div>
                  : <div />
              }
              {closeable || asModal
                ? <div className="closeWindow-div" onClick={closeWrapper}>
                  <FontAwesomeIcon
                    icon={['fas', 'times']}
                    aria-label={`${title} window close icon`}
                    className="close-button"
                  />
                </div>
                : <div />
              }
            </div>
          </div>
        </div>
        <div className="window-content">{windowContent}</div>
        <div
          style={footerStyle}
          className={`list-group-item active resize-footer ${isLocked ? 'locked' : ''}`}
        >
          {resizeable || asModal
            ? <FontAwesomeIcon
              icon={['far', 'arrows-alt-h']}
              transform={{ rotate: 45 }}
              className="resizeable-handle"
              aria-label={`${title} window resize icon`}
              onClick={() => setPrevSize(null) /* bring back maximize button */}
            />
            : null
          }
        </div>
      </div>
    </div>
  )
})

Window.propTypes = {
  // //////////////////////////////////////////////////////////////////////
  // Note windows must be run in the parent WindowContainer component
  // //////////////////////////////////////////////////////////////////////

  // classname to apply to outermost window div
  className: PropTypes.string,
  // Text to display in window header (overrides window config if present)
  title: PropTypes.string,
  // Bool to determine if loading spinner should be rendered instead of children
  loading: PropTypes.bool,
  // Config details to be applied to the window
  // This is automatically passed in by WindowContainer
  windowConfig: PropTypes.shape({
    // Width to display window in px (after conversion from percent by windowContainer)
    width: PropTypes.number,
    // Height to display window in px (after conversion from percent by windowContainer)
    height: PropTypes.number,
    // X position of the upper left window corner in px (after conversion from percent by windowContainer)
    x: PropTypes.number,
    // Y position of the upper left window corner  in px (after conversion from percent by windowContainer)
    y: PropTypes.number,
    // User visible name of the window
    name: PropTypes.string,
    // the ref attached to the window component within the WindowContainer component
    ref: PropTypes.string.isRequired,
    // Should the window be visible in the window menu
    selectable: PropTypes.bool
  }),
  // If present, questionMark will be displayed in the upper right corner and will
  // display text associated with the helpId string in the data/help.js file
  helpId: PropTypes.string,
  // should the window have a close X in the upper right
  closeable: PropTypes.bool,
  // Should the window be draggable and have drag handle in upper left
  draggable: PropTypes.bool,
  // Should the window be resizable and have arrow handle at lower right
  resizeable: PropTypes.bool,
  // function to handle managing the zIndex of multiple windows open at any time
  // this is automatically applied by WindowContainer
  zIndexHandler: PropTypes.func.isRequired,
  // function to attach to the close X in the upper right corner
  // this is automatically applied by WindowContainer
  closeHandler: PropTypes.func.isRequired,
  // Child component to be rendered within the window window
  children: PropTypes.oneOfType([PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired
}
export default Window
